import { Component, Prop, Method, Event, EventEmitter, Host, State, h } from '@stencil/core';

/**
 * Badges help draw attention to important content and can provide additional context to UI elements.
 */
@Component({
  tag: 'som-badge',
  styleUrl: 'som-badge.scss',
  shadow: true,
})
export class SomBadge {

  /******
   * PROPS
   ******/

  /**
   *  This property shows the size of badge.
   */
  @Prop() size: "xs" | "sm" | "md" | "lg" = "md";

  /**
   * This property shows the color of the badge.
   */
  @Prop() color: "primary" | "secondary" | "gray" | "danger" | "warning" | "success" | "info" = "gray";

  /**
   * This property shows the text to be displayed in badge.
   */
  @Prop() label: string;

  /**
   * This boolean determines if a badge can be dismissed.
   */
  @Prop() dismissible: boolean = false;

  /**
   * This property determines which icon should be used. The property should match an icon name such as `checkmark`.
   */
  @Prop() icon: string;

  /**
   * This boolean shows the determines whether to use the light or dark theme.
   */
  @Prop() light: boolean = false;

  /**
   * This property overrides the close button aria label.
   */
  @Prop() closeButtonAriaLabel: string = "dismiss";
  


  /*****
   * EVENTS
   *****/

  /**
   * This event is emitted when the badge is dismissed.
   */
  @Event() somDismissed!: EventEmitter<void>;

  /**
  * State of dismiss
  */
  @State() isHidden: boolean = false;


  /*******
   * PUBLIC METHODS
   *******/

  /**
   * This public method is used to show a badge.
   * @returns Promise will resolve after badge is shown.
   */
  @Method()
  async show(): Promise<void> {
    return new Promise(resolve => {
      this.isHidden = false;
      resolve();
    });
  }

  /**
   * This public method is used to hide a badge.
   * @returns Promise will resolve after badge is hidden.
   */
  @Method()
  async hide(): Promise<void> {
    return new Promise(resolve => {
      this.isHidden = true;
      this.somDismissed.emit();
      resolve();
    });
  }

  render() {
    return (
      <Host class={`d-inline-block ${(this.isHidden) ? 'badge-dismissed' : ''}`}>
        <span class={`badge badge--${this.size} badge--${this.color} ${(this.dismissible && this.size !== 'xs') ? 'badge--dismissible' : ''} ${this.light ? 'badge--light' : ''}`}>
          <span class='badge__content'>
            {(this.icon && this.size !== 'xs') ? <span class="badge__icon" innerHTML={`<som-icon name="${this.icon}" display="block"></som-icon>`}></span> : null}
            {(this.label) ?
              <span class={`${((this.icon && this.size !== 'xs') ? 'badge__content--icon' : '')} ${((this.dismissible && this.size !== 'xs') ? 'badge__content--dismissible' : '')}`}>
                {this.label}
              </span>
              : null}
            {(this.dismissible && this.size !== 'xs') ?
              <button type="button" class="badge__button" onClick={() => this.hide()} aria-label={this.closeButtonAriaLabel}>
                <som-icon name="close" display="block" aria-hidden="true"></som-icon>
              </button>
              : null}
          </span>
        </span>
      </Host>
    );
  }
}